import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { teleconsultUrl, TELECONSULT } from "@/lib/constants";

const DISMISS_KEY = "med_fab_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24; // 24h

export const FloatingReviewCTA = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    // Respect a 24h dismiss window
    const stored = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (stored && Date.now() - stored < DISMISS_TTL_MS) return;

    // Show after user has scrolled a bit or after 4s idle — feels less pushy
    const timer = setTimeout(() => setVisible(true), 4000);
    const onScroll = () => {
      if (window.scrollY > 320) {
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // On small screens, collapse to icon-only after 6s to reduce clutter
  useEffect(() => {
    if (!visible) return;
    const isSmall = window.matchMedia("(max-width: 767px)").matches;
    if (!isSmall) return;
    const t = setTimeout(() => setExpanded(false), 6000);
    return () => clearTimeout(t);
  }, [visible]);

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const trackClick = () => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        method: "whatsapp_floating_review_cta",
        value: 1,
      });
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="floating-review-cta"
      className="fixed z-40 bottom-5 right-5 md:bottom-7 md:right-7 flex items-center gap-2 group"
    >
      <a
        href={teleconsultUrl(TELECONSULT.reportReviewMessage)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackClick}
        data-testid="floating-review-cta-btn"
        aria-label="Book a 15-minute report review via WhatsApp"
        onMouseEnter={() => setExpanded(true)}
        onFocus={() => setExpanded(true)}
        className={`relative inline-flex items-center gap-3 bg-[#0A2540] text-white shadow-[0_20px_50px_-15px_rgba(10,37,64,0.6)] border border-[#B08A4A] hover:bg-[#B08A4A] hover:text-white transition-all duration-500 rounded-full pl-4 pr-5 py-3.5 ${
          expanded ? "" : "md:pr-5"
        }`}
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#B08A4A]/25 shrink-0">
          <span className="absolute inset-0 rounded-full bg-[#B08A4A]/40 animate-ping" />
          <MessageCircle
            size={16}
            strokeWidth={1.7}
            className="relative text-[#B08A4A] group-hover:text-white"
          />
        </span>
        <span
          className={`transition-[max-width,opacity,margin] duration-500 overflow-hidden whitespace-nowrap font-sans text-[11px] uppercase tracking-widest-plus font-semibold ${
            expanded
              ? "max-w-[260px] opacity-100 ml-0"
              : "max-w-0 opacity-0 -ml-2 md:max-w-[260px] md:opacity-100 md:ml-0"
          }`}
        >
          Book a 15-min Report Review
        </span>
      </a>

      <button
        type="button"
        onClick={dismiss}
        data-testid="floating-review-cta-close"
        aria-label="Dismiss"
        className="hidden md:inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-[#E2E8F0] text-[#94A3B8] hover:text-[#0A2540] hover:border-[#0A2540] transition-colors opacity-0 group-hover:opacity-100"
      >
        <X size={12} strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default FloatingReviewCTA;
